/**
 * Utility functions for sorting dropdown options
 * 
 * Applies alphabetical sorting (A-Z) to all dropdown options
 * with special handling for "Other"/"Others" which always appears at the end
 */

/**
 * Sort an array of strings alphabetically, with "Other"/"Others" at the end
 * @param {string[]} options - Array of string options
 * @returns {string[]} - Sorted array with "Other"/"Others" at end
 */
export const sortStringOptions = (options) => {
  if (!options || !Array.isArray(options)) return options;
  
  // Separate "Other" and "Others" from the rest
  const othersOptions = [];
  const regularOptions = [];
  
  options.forEach(opt => {
    if (typeof opt === 'string' && (opt.toLowerCase() === 'other' || opt.toLowerCase() === 'others')) {
      othersOptions.push(opt);
    } else {
      regularOptions.push(opt);
    }
  });
  
  // Sort regular options alphabetically (case-insensitive)
  regularOptions.sort((a, b) => {
    if (typeof a === 'string' && typeof b === 'string') {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    }
    return 0;
  });
  
  // Append "Other"/"Others" at the end
  return [...regularOptions, ...othersOptions];
};

/**
 * Sort an array of objects alphabetically by a specified key, with "Other"/"Others" at the end
 * @param {Object[]} options - Array of option objects
 * @param {string} labelKey - The key to use for the label (e.g., 'department_name', 'job_function_name', 'name')
 * @returns {Object[]} - Sorted array with "Other"/"Others" items at end
 */
export const sortObjectOptions = (options, labelKey = 'name') => {
  if (!options || !Array.isArray(options)) return options;
  
  // Separate "Other" and "Others" from the rest
  const othersOptions = [];
  const regularOptions = [];
  
  options.forEach(opt => {
    const label = opt[labelKey];
    if (typeof label === 'string' && (label.toLowerCase() === 'other' || label.toLowerCase() === 'others')) {
      othersOptions.push(opt);
    } else {
      regularOptions.push(opt);
    }
  });
  
  // Sort regular options alphabetically by the label key (case-insensitive)
  regularOptions.sort((a, b) => {
    const labelA = a[labelKey];
    const labelB = b[labelKey];
    if (typeof labelA === 'string' && typeof labelB === 'string') {
      return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
    }
    return 0;
  });
  
  // Append "Other"/"Others" at the end
  return [...regularOptions, ...othersOptions];
};

/**
 * Generic sort function that works with both strings and objects
 * Auto-detects the type and applies appropriate sorting
 * @param {Array} options - Array of options (strings or objects)
 * @param {string} [labelKey] - For objects, the key to sort by
 * @returns {Array} - Sorted array with "Other"/"Others" at end
 */
export const sortDropdownOptions = (options, labelKey = null) => {
  if (!options || !Array.isArray(options) || options.length === 0) return options;
  
  // Check if first item is a string or object
  const firstItem = options[0];
  
  if (typeof firstItem === 'string') {
    return sortStringOptions(options);
  }
  
  if (typeof firstItem === 'object' && firstItem !== null) {
    // Try to auto-detect the label key if not provided
    const detectedKey = labelKey || 
      (firstItem.department_name ? 'department_name' : 
       firstItem.job_function_name ? 'job_function_name' :
       firstItem.name ? 'name' :
       firstItem.label ? 'label' :
       firstItem.title ? 'title' :
       Object.keys(firstItem).find(key => 
         typeof firstItem[key] === 'string' && 
         key.toLowerCase().includes('name')
       ) || Object.keys(firstItem)[0]);
    
    return sortObjectOptions(options, detectedKey);
  }
  
  return options;
};

export default sortDropdownOptions;

/**
 * ============================================
 * CENTRALIZED INSTITUTION TYPES
 * ============================================
 * Single source of truth for institution/organization types
 * Used across CRM (Lead creation), Signup, and all other dropdowns
 * 
 * Note: "Other" is automatically placed at the end by sortStringOptions
 */
export const INSTITUTION_TYPES = sortStringOptions([
  'Clinic',
  'Diagnostic Center',
  'Healthcare Network',
  'Hospital (Small / Medium)',
  'Hospital (Large)',
  'Individual',
  'Medical Center',
  'Nursing Home',
  'Rehabilitation Center',
  'Research Institution',
  'Academic Institution',
  'Other'
]);

/**
 * Get institution types for CRM Lead creation
 * Excludes "Individual" as leads are for organizations
 */
export const getCRMInstitutionTypes = () => {
  return INSTITUTION_TYPES.filter(type => type !== 'Individual');
};

/**
 * Get institution types for Signup flow
 * Includes all types
 */
export const getSignupInstitutionTypes = () => {
  return INSTITUTION_TYPES;
};
